import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { GraphLayout } from '@/types';

// ─── Panel types ──────────────────────────────────────────────────────────────

export type ActivePanel =
  | 'none'
  | 'ai-suggestions'
  | 'ai-search'
  | 'capability-detail'
  | 'graph-filters'
  | 'quick-capture';

export type Theme = 'light' | 'dark' | 'system';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  variant: ToastVariant;
  duration?: number;
}

// ─── State shape ──────────────────────────────────────────────────────────────

interface UIState {
  sidebarOpen: boolean;
  sidebarWidth: number;
  activePanel: ActivePanel;
  commandPaletteOpen: boolean;
  quickCaptureOpen: boolean;
  theme: Theme;
  graphLayout: GraphLayout;
  graphFilterTypes: string[];
  graphFilterStatuses: string[];
  selectedCapabilityId: string | null;
  selectedNodeIds: string[];
  toasts: Toast[];
  isMobile: boolean;
}

interface UIActions {
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  setSidebarWidth: (width: number) => void;
  setActivePanel: (panel: ActivePanel) => void;
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;
  openQuickCapture: () => void;
  closeQuickCapture: () => void;
  setTheme: (theme: Theme) => void;
  setGraphLayout: (layout: GraphLayout) => void;
  setGraphFilterTypes: (types: string[]) => void;
  setGraphFilterStatuses: (statuses: string[]) => void;
  toggleGraphFilterType: (type: string) => void;
  setSelectedCapabilityId: (id: string | null) => void;
  setSelectedNodeIds: (ids: string[]) => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  setIsMobile: (mobile: boolean) => void;
}

type UIStore = UIState & UIActions;

// ─── Nano ID helper (inline to avoid import) ──────────────────────────────────

let _toastCounter = 0;
function toastId(): string {
  return `toast-${Date.now()}-${++_toastCounter}`;
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      // State
      sidebarOpen: true,
      sidebarWidth: 256,
      activePanel: 'none',
      commandPaletteOpen: false,
      quickCaptureOpen: false,
      theme: 'system',
      graphLayout: 'fcose',
      graphFilterTypes: [],
      graphFilterStatuses: [],
      selectedCapabilityId: null,
      selectedNodeIds: [],
      toasts: [],
      isMobile: false,

      // Sidebar
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
      setSidebarWidth: (width) => set({ sidebarWidth: width }),

      // Panel
      setActivePanel: (panel) => set({ activePanel: panel }),

      // Command palette
      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),
      toggleCommandPalette: () =>
        set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),

      // Quick capture
      openQuickCapture: () => set({ quickCaptureOpen: true }),
      closeQuickCapture: () => set({ quickCaptureOpen: false }),

      // Theme
      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },

      // Graph
      setGraphLayout: (graphLayout) => set({ graphLayout }),
      setGraphFilterTypes: (graphFilterTypes) => set({ graphFilterTypes }),
      setGraphFilterStatuses: (graphFilterStatuses) => set({ graphFilterStatuses }),
      toggleGraphFilterType: (type) => {
        const current = get().graphFilterTypes;
        const next = current.includes(type)
          ? current.filter((t) => t !== type)
          : [...current, type];
        set({ graphFilterTypes: next });
      },

      // Selection
      setSelectedCapabilityId: (id) =>
        set({ selectedCapabilityId: id, activePanel: id ? 'capability-detail' : 'none' }),
      setSelectedNodeIds: (ids) => set({ selectedNodeIds: ids }),

      // Toasts
      addToast: (toast) => {
        const id = toastId();
        set((s) => ({ toasts: [...s.toasts, { ...toast, id }] }));
        const duration = toast.duration ?? 4000;
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },
      removeToast: (id) =>
        set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

      // Mobile
      setIsMobile: (isMobile) =>
        set({ isMobile, sidebarOpen: !isMobile }),
    }),
    {
      name: 'capOS-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        sidebarWidth: state.sidebarWidth,
        theme: state.theme,
        graphLayout: state.graphLayout,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
    }
  )
);

// ─── Theme helper ─────────────────────────────────────────────────────────────

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const shouldBeDark = theme === 'dark' || (theme === 'system' && prefersDark);

  if (shouldBeDark) {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectSidebarOpen = (s: UIStore) => s.sidebarOpen;
export const selectCommandPaletteOpen = (s: UIStore) => s.commandPaletteOpen;
export const selectActivePanel = (s: UIStore) => s.activePanel;
export const selectTheme = (s: UIStore) => s.theme;
export const selectGraphLayout = (s: UIStore) => s.graphLayout;
export const selectSelectedCapabilityId = (s: UIStore) => s.selectedCapabilityId;
export const selectToasts = (s: UIStore) => s.toasts;
